import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AxiosError } from 'axios';
import { FeedbackType, sendFeedback } from 'src/api/feedback';
import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { feedbackCreated } from 'src/store/feedbacks/slice';
import { messagesActions } from 'src/store/messages/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

export interface FeedbackDialogContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  feedbackType: FeedbackType;
  setFeedbackType: (value: FeedbackType) => void;
  message: string | undefined;
  setMessage: (value: string) => void;
  submit: () => Promise<void>;
}

export const FeedbackDialogContext = createContext<
  FeedbackDialogContextValue | undefined
>(undefined);
FeedbackDialogContext.displayName = 'FeedbackDialogContext';

interface FeedbackDialogProviderProps {
  children?: ReactNode;
}

export const FeedbackDialogProvider: FC<FeedbackDialogProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(
    FeedbackType.Question,
  );
  const [message, setMessage] = useState<string>('');
  const openedTimes = useRef(0);

  const reset = useCallback(() => {
    setFeedbackType(FeedbackType.Question);
    setMessage('');
  }, []);

  const open = useCallback(() => {
    openedTimes.current++;
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    reset();
  }, [reset]);

  const submit = useCallback(async () => {
    const openedCounter = openedTimes.current;

    try {
      await sendFeedback({
        typeId: feedbackType,
        message,
      });

      dispatch(feedbackCreated());

      if (openedCounter === openedTimes.current) close();
    } catch (e) {
      dispatch(
        messagesActions.show({
          message: (e as AxiosError).response?.data as string,
        }),
      );
    }
  }, [close, dispatch, feedbackType, message]);

  const contextValue = useMemo((): FeedbackDialogContextValue => {
    return {
      isOpen,
      open,
      close,
      feedbackType,
      setFeedbackType,
      message,
      setMessage,
      submit,
    };
  }, [close, feedbackType, isOpen, message, open, submit]);

  return (
    <FeedbackDialogContext.Provider value={contextValue}>
      {children}
    </FeedbackDialogContext.Provider>
  );
};

export const useFeedbackDialog = (): FeedbackDialogContextValue =>
  useRequiredContext(FeedbackDialogContext);
